import {hreflangKeywords, supportedHrefLanguages} from "@/lang/hreflangKeywords";
import { supportedLanguages } from '@/config';
import {locationsList} from "@/new-constants/locationsList";
import {normalizeUrl} from "@/Utils/Utils";

// Helper function to create and append <link> tags if they do not already exist
function createLinkTag(rel, hreflang, href) {
    if (!document.querySelector(`link[hreflang="${hreflang}"]`)) {
        const linkTag = document.createElement('link');
        linkTag.setAttribute('rel', rel);
        if (hreflang) linkTag.setAttribute('hreflang', hreflang); // Avoid adding hreflang for canonical tags
        linkTag.setAttribute('href', normalizeUrl(href));
        document.head.appendChild(linkTag);
    }
}

export function addHreflangTags(currentUrl) {
    const baseUrl = process.env.VUE_APP_SITE_URL;

    // Generate links only if the current URL matches the language pattern
    supportedHrefLanguages.forEach(lang => {
        const keyword = hreflangKeywords[lang];
        const pattern = `/${lang}/accommodation/${keyword}`;

        if (currentUrl.includes(pattern)) {
            const [before, after] = currentUrl.split(pattern);

            // Generate hreflang tags for all supported languages
            supportedHrefLanguages.forEach(lang => {
                const keyword = hreflangKeywords[lang];
                const newUrl = `${baseUrl}${before}/${lang}/accommodation/${keyword}${after}`;
                createLinkTag('alternate', lang, newUrl);
            });

            // Add x-default hreflang tag pointing to Spanish ("es") as the default language
            const defaultUrl = `${baseUrl}${before}/es/accommodation/${hreflangKeywords['es']}${after}`;
            createLinkTag('alternate', 'x-default', defaultUrl);
        }
    });
}

export function addCanonicalTag() {
    const canonicalUrl = `${window.location.origin}${window.location.pathname}`;
    createLinkTag('canonical', null, canonicalUrl);
}

// Main function to get related city links based on the current location
export function getRelatedCitiesLinks(currentLocation) {
    
    const baseUrl = process.env.VUE_APP_SITE_URL;
    const currentLanguage = getCurrentLanguage();

    // Filter and select related cities
    const relatedCities = getRelatedCities(currentLocation);

    // Generate links for each related city
    return relatedCities.map(location => buildCityLink(location, currentLanguage, baseUrl));
}

// Main function to get related city links based on the current location
export function getOthersCitiesLinks() {
    
    const baseUrl = process.env.VUE_APP_SITE_URL;

    const browserLanguage = navigator.language || navigator.userLanguage;
    const currentSupportedLanguage = getClosestLanguage(browserLanguage);
    
    // Filter and select related cities
    const otherCities = getOthersCities();

    // Generate links for each related city
    return otherCities.map(location => buildCityLink(location, currentSupportedLanguage, baseUrl));
}

// Gets the current language from the URL
function getCurrentLanguage() {
    return window.location.pathname.split('/')[1]; // Example: 'es-ES'
}

// Get closest supported language based on the browser language
function getClosestLanguage(language) {
    // Check for an exact match
    if (supportedLanguages.includes(language)) {
        return language;
    }

    // Check for a close match based on the language prefix (e.g., "es" in "es-AR")
    const languagePrefix = language.split('-')[0];
    const closestMatch = supportedLanguages.find((lang) =>
        lang.startsWith(languagePrefix)
    );

    // Return the closest match or a default value
    return closestMatch || 'en'; // Default to 'en' if no match is found
}

// Filters and selects related cities in the same country with `search_query_enabled`
function getRelatedCities(currentLocation) {
    return locationsList
        .filter(location =>
            location.country === currentLocation.country &&
            location.search_query_enabled
        )
        .sort(() => 0.5 - Math.random()) // Randomly shuffle the list
        .slice(0, 3); // Limit to the first 3 cities
}

// Filters and selects other cities with `search_query_enabled`
function getOthersCities() {
    return locationsList
        .filter(location =>
            location.search_query_enabled &&
            location.home_search_query_enabled
        )
        .sort(() => 0.5 - Math.random()) // Randomly shuffle the list
        .slice(0, 5); // Limit to the first 3 cities
}

// Builds the link for each city with the necessary parameters
function buildCityLink(location, language, baseUrl) {
    const keyword = hreflangKeywords[language];
    const { city, neighborhood } = getCityAndNeighborhood(location.comuna);

    // Include `neighborhood` in the path only if it is not empty
    const path = neighborhood
        ? `/${language}/accommodation/${keyword}_${city}_${neighborhood}`
        : `/${language}/accommodation/${keyword}_${city}`;

    const url = `${baseUrl}${path}`;
    const name = location.comuna;
    const image = loadCityImage(location.image_filename);

    return { url, name, image };
}

// Extracts and formats city and neighborhood from `location.comuna`
function getCityAndNeighborhood(comuna) {
    let parts = comuna.toLowerCase()
        .split(', ')
        .slice(-2)
        .map(part => part.trim().replace(/[ ]+/g, '-'));

    // If there is only one part, use it as `city` and leave `neighborhood` empty
    const city = parts[0];
    const neighborhood = parts[1] || ''; // Use an empty string if `neighborhood` is absent

    return { city, neighborhood };
}

// Dynamically loads the city image
export function loadCityImage(filename) {
    return require(`@/new-assets/locations_images/${filename}.jpg`);
}

export function setDescriptionHeader(description) {
    // Create or replace meta description
    let descriptionTag = document.querySelector('meta[name="description"]');

    if (!descriptionTag) {
        // If the meta description tag does not exist, create it
        descriptionTag = document.createElement('meta');
        descriptionTag.setAttribute('name', 'description');
        document.head.appendChild(descriptionTag);
    }

    // Set the content of the meta description tag
    descriptionTag.setAttribute('content', description);
}

// ─── Datos estructurados (JSON-LD) ────────────────────────────────────────────
// Google lee este bloque tras renderizar la SPA y lo usa para los "rich results"
// de la ficha. Se inserta un único <script> con id fijo que se reemplaza en cada
// alojamiento y se elimina al salir, para que una ficha no herede el schema de
// otra ni queden bloques duplicados en el <head>.

const JSON_LD_ID = 'hyf-jsonld-accommodation';

/**
 * Inserta (o reemplaza) el JSON-LD de la ficha de alojamiento.
 *
 * Recibe un objeto ya "aplanado" por el componente (título, descripción,
 * precio, etc.) y arma un schema de tipo `Accommodation` con una `Offer`.
 * Cualquier campo ausente se omite: nunca se publican `null`, `undefined`,
 * strings vacíos ni precios en 0, porque un dato basura en el schema es peor
 * que no tenerlo (Google marca el rich result como inválido).
 *
 * No lanza: si algo falla, se traga el error y la ficha sigue funcionando. El
 * JSON-LD es un extra de SEO, nunca debe tumbar la vista.
 */
export function setAccommodationJsonLd(data) {
    try {
        if (!data || typeof document === 'undefined') return;

        const schema = {
            '@context': 'https://schema.org',
            '@type': 'Accommodation',
        };

        if (notEmpty(data.name)) schema.name = String(data.name);
        if (notEmpty(data.description)) schema.description = String(data.description);
        if (notEmpty(data.url)) schema.url = String(data.url);
        if (notEmpty(data.sku)) schema.sku = String(data.sku);

        const images = (Array.isArray(data.images) ? data.images : [])
            .filter(notEmpty)
            .map(String);
        if (images.length) schema.image = images;

        if (positive(data.floorSize)) {
            schema.floorSize = {
                '@type': 'QuantitativeValue',
                value: Number(data.floorSize),
                unitCode: 'MTK', // metros cuadrados (UN/CEFACT)
            };
        }

        if (positive(data.occupancy)) {
            schema.occupancy = {
                '@type': 'QuantitativeValue',
                maxValue: Number(data.occupancy),
                unitText: 'person',
            };
        }

        const address = buildPostalAddress(data.addressText);
        if (address) schema.address = address;

        const offer = buildOffer(data);
        if (offer) schema.offers = offer;

        upsertJsonLdScript(schema);
    } catch (e) {
        // El schema es opcional: ante cualquier error no rompemos la ficha.
        console.error('[HyF][SEO] No se pudo generar el JSON-LD:', e);
    }
}

/**
 * Quita el JSON-LD de la ficha. Se llama al destruir el componente para que la
 * siguiente vista (home, búsqueda, otra ficha) no arrastre este schema.
 */
export function removeAccommodationJsonLd() {
    if (typeof document === 'undefined') return;
    const existing = document.getElementById(JSON_LD_ID);
    if (existing && existing.parentNode) {
        existing.parentNode.removeChild(existing);
    }
}

function upsertJsonLdScript(schema) {
    let script = document.getElementById(JSON_LD_ID);
    if (!script) {
        script = document.createElement('script');
        script.type = 'application/ld+json';
        script.id = JSON_LD_ID;
        document.head.appendChild(script);
    }
    script.textContent = JSON.stringify(schema);
}

/**
 * `Offer` solo si hay un precio real (> 0). `price` va como string y sin
 * separadores de miles, como exige schema.org; la moneda debe ser un código
 * ISO 4217 de 3 letras ("CLP", "EUR"), si no, se omite para no invalidar.
 */
function buildOffer(data) {
    const price = Number(data.price);
    if (!Number.isFinite(price) || price <= 0) return null;

    const offer = {
        '@type': 'Offer',
        price: String(price),
        availability: 'https://schema.org/InStock',
    };

    const currency = normalizeCurrency(data.currency);
    if (currency) offer.priceCurrency = currency;

    if (notEmpty(data.url)) offer.url = String(data.url);

    return offer;
}

function buildPostalAddress(addressText) {
    if (!notEmpty(addressText)) return null;
    return {
        '@type': 'PostalAddress',
        name: String(addressText).trim(),
    };
}

function normalizeCurrency(currency) {
    const code = String(currency || '').trim().toUpperCase();
    return /^[A-Z]{3}$/.test(code) ? code : '';
}

function notEmpty(value) {
    return value !== null && value !== undefined && String(value).trim() !== '';
}

function positive(value) {
    return Number.isFinite(Number(value)) && Number(value) > 0;
}

